import React, { useEffect, useMemo, useState } from "react";
import { SCENES, isChoiceAvailable } from "../../game/scenes";
import { MEMORY_KEYS } from "../../game/memories";
import { audio } from "../../game/audio";
import { StatusBars } from "./SanityMeter";
import { InventoryPanel } from "./InventoryPanel";
import { JournalPanel } from "./JournalPanel";
import { MemoriesPanel } from "./MemoriesPanel";
import { AudioSettings } from "./AudioSettings";
import { Package, BookOpen, Skull, Home, Film, Settings2 } from "lucide-react";

export const GameScene = ({ state, auth, onChoose, onExit, onSummonWhisper, onPlayMemory }) => {
  const scene = SCENES[state.scene_id] || SCENES.start;
  const [invOpen, setInvOpen] = useState(false);
  const [jrnOpen, setJrnOpen] = useState(false);
  const [memOpen, setMemOpen] = useState(false);
  const [audOpen, setAudOpen] = useState(false);

  // Switch ambient audio + speak new scene when scene changes
  useEffect(() => {
    audio.startAmbient(scene.mode || "exploration");
    // Speak the scene narration in a clean voice
    if (scene.text && scene.text.length) {
      audio.speak(scene.text.join(" "));
    }
    return () => audio.cancelSpeech();
  }, [state.scene_id, scene.mode, scene.text]);

  // Heartbeat when danger, or when sanity + composure are both critically low
  useEffect(() => {
    const critical = scene.mode === "danger" || (state.sanity < 30 && state.composure < 30);
    audio.heartbeat(critical);
    return () => audio.heartbeat(false);
  }, [scene.mode, state.sanity, state.composure]);

  const bg = scene.bg;
  const stealth = scene.mode === "stealth";
  const danger = scene.mode === "danger";
  const grief = scene.mode === "grief";
  const safe = scene.mode === "safe";
  const credits = scene.mode === "credits";

  const availableChoices = useMemo(
    () => scene.choices.map((c) => ({ ...c, _enabled: isChoiceAvailable(c, state) })),
    [scene, state],
  );

  return (
    <div className={`relative min-h-screen w-full overflow-hidden ${danger ? "animate-heartbeat" : ""}`} data-testid="game-scene">
      <div
        key={state.scene_id}
        className="absolute inset-0 bg-cover bg-center animate-fadein"
        style={{
          backgroundImage: `url(${bg})`,
          filter: danger
            ? "brightness(0.32) saturate(1.4) hue-rotate(-10deg) contrast(1.2)"
            : stealth
            ? "brightness(0.22) saturate(0.4) contrast(1.1)"
            : credits
            ? "brightness(0.4) saturate(0.7) contrast(1.05)"
            : "brightness(0.32) saturate(0.55) contrast(1.15)",
        }}
        aria-hidden
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/85 to-black/40" aria-hidden />
      {danger && <div className="absolute inset-0 bg-emergency/10 mix-blend-multiply" aria-hidden />}
      {grief && <div className="absolute inset-0 bg-black/40" aria-hidden />}
      {safe && <div className="absolute inset-0 bg-black/30" aria-hidden />}

      {/* HUD */}
      <div className="relative z-10 flex items-start justify-between px-6 md:px-12 py-6 border-b border-white/5 gap-6">
        <div className="flex items-center gap-6 pt-1">
          <button onClick={onExit} className="text-white/40 hover:text-emergency transition-colors" data-testid="hud-exit" title="Return to menu">
            <Home size={16} />
          </button>
          <button onClick={() => setAudOpen(true)} className="text-white/40 hover:text-emergency transition-colors" data-testid="hud-audio" title="Audio settings">
            <Settings2 size={16} />
          </button>
          {auth && (auth.user || auth.guest) && (
            <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-white/50 flex items-center gap-2" data-testid="hud-username">
              <span className="w-1.5 h-1.5 rounded-full bg-emergency" aria-hidden />
              {auth.user ? auth.user.username : "GUEST"}
            </div>
          )}
          <div className="font-mono text-[10px] tracking-[0.35em] uppercase text-white/40" data-testid="hud-chapter">
            ACT {scene.act} <span className="text-white/20 mx-2">/</span> {scene.chapter}
          </div>
        </div>
        <div className="flex items-start gap-6">
          <StatusBars sanity={state.sanity} composure={state.composure} corruption={state.corruption} />
          <div className="flex flex-col gap-2 pt-0.5">
            <button onClick={() => setInvOpen(true)} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors" data-testid="hud-inventory">
              <Package size={14} />
              <span className="font-mono text-xs">{state.inventory.length}</span>
            </button>
            <button onClick={() => setJrnOpen(true)} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors" data-testid="hud-journal">
              <BookOpen size={14} />
              <span className="font-mono text-xs">{state.journal.length}</span>
            </button>
            <button onClick={() => setMemOpen(true)} className="flex items-center gap-2 text-white/50 hover:text-white transition-colors" data-testid="hud-memories" title="Memories">
              <Film size={14} />
              <span className="font-mono text-xs">
                {MEMORY_KEYS.filter((k) => state.flags?.[k]).length}/{MEMORY_KEYS.length}
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative z-10 grid grid-rows-[1fr_auto] min-h-[calc(100vh-96px)]">
        <div className="px-6 md:px-16 lg:px-24 py-14 max-w-4xl">
          <div className="mb-8">
            <p className="font-mono text-[10px] tracking-[0.45em] uppercase text-emergency/70 mb-4 flex items-center gap-3" data-testid="scene-mode">
              {danger && <><Skull size={12} /> HOSTILE PRESENCE</>}
              {stealth && <>STEALTH — REMAIN UNSEEN</>}
              {grief && <>— A MOMENT —</>}
              {safe && <>SAFE ROOM</>}
              {credits && <>— END —</>}
              {!danger && !stealth && !grief && !safe && !credits && <>EXPLORATION</>}
            </p>
          </div>
          <div className="narrative text-lg md:text-xl text-white/85 max-w-2xl space-y-6" data-testid="scene-text">
            {scene.text.map((line, i) => (
              <p
                key={`${state.scene_id}-${i}`}
                className={i === 0 && !credits ? "text-xl md:text-2xl text-white first-letter:text-3xl first-letter:mr-1 first-letter:text-emergency/80 first-letter:font-serif first-letter:italic" : ""}
              >
                {line}
              </p>
            ))}
          </div>
        </div>

        {/* Choices */}
        <div className="px-6 md:px-16 lg:px-24 pb-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-4xl">
            {availableChoices.map((c, i) => (
              <button
                key={`${state.scene_id}-c${i}`}
                className={`term-btn ${danger ? "term-btn-danger" : ""}`}
                disabled={!c._enabled}
                onClick={() => c._enabled && onChoose(c)}
                data-testid={`choice-${i}`}
              >
                <span className="block">
                  {c.label}
                  {!c._enabled && c.requires_item && <span className="block text-[10px] text-emergency/60 mt-1 font-mono">// requires: {c.requires_item}</span>}
                  {!c._enabled && c.requires && <span className="block text-[10px] text-emergency/60 mt-1 font-mono">// requires: {c.requires.join(" + ")}</span>}
                  {!c._enabled && c.requires_flag && <span className="block text-[10px] text-emergency/60 mt-1 font-mono">// requires: prior knowledge</span>}
                  {!c._enabled && c.requires_all && <span className="block text-[10px] text-emergency/60 mt-1 font-mono">// requires: all trap components</span>}
                </span>
              </button>
            ))}
          </div>
          {state.sanity < 40 && (
            <button
              onClick={() => onSummonWhisper("ambient")}
              className="mt-6 text-xs font-mono text-emergency/60 hover:text-emergency italic tracking-widest"
              data-testid="listen-btn"
            >
              // listen closer...
            </button>
          )}
        </div>
      </div>

      <InventoryPanel open={invOpen} onClose={() => setInvOpen(false)} items={state.inventory} />
      <JournalPanel open={jrnOpen} onClose={() => setJrnOpen(false)} entries={state.journal} />
      <MemoriesPanel open={memOpen} onClose={() => setMemOpen(false)} flags={state.flags || {}} onPlay={onPlayMemory} />
      <AudioSettings open={audOpen} onClose={() => setAudOpen(false)} />
    </div>
  );
};
